import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowRight, Rocket, Users, Sparkles, CheckCircle2, Globe2, Radio,
  TrendingUp, LineChart, Handshake, Percent, Layers
} from "lucide-react";
import Seo from "@/components/seo/Seo";
import api from "@/lib/api";
import HeroVideo from "@/components/common/HeroVideo";
import { GlowOrb } from "@/components/common/GlowOrb";

/**
 * Partnership page — CloudLab offers TWO partnership programs:
 *   1. Technology Partners    — platforms co-building on our stack
 *   2. Channel & Reseller     — regional partners taking us to market
 *
 * Hero renders a landscape 16:9 video pulled from Admin → Page Videos
 * (`page-media/partnership-hero`). Falls back to an animated aurora canvas
 * when no URL is set.
 */

const PARTNER_PROGRAMS = [
  {
    id: "technology",
    icon: Rocket,
    kicker: "Program 01",
    title: "Technology Partners",
    tagline: "Co-build on the CloudLab AI stack.",
    description:
      "For cloud platforms, AI infrastructure, SaaS products and vertical software teams that want CloudLab engineering embedded inside their offering — from joint architecture reviews to co-shipped features.",
    ideal: [
      "Cloud & AI infrastructure companies",
      "Vertical SaaS platforms scaling into AI",
      "Enterprise software vendors adding autonomous agents",
      "ISVs looking for a delivery arm",
    ],
    perks: [
      "Joint architecture and solution design",
      "Priority engineering pods with shared SLAs",
      "Product co-marketing and joint case studies",
      "Access to CloudLab's private roadmap",
    ],
    color: "primary",
  },
  {
    id: "channel",
    icon: Users,
    kicker: "Program 02",
    title: "Channel & Reseller Partners",
    tagline: "Take our products to your market — earn on every deal.",
    description:
      "For agencies, boutique consultancies and regional distributors ready to resell CloudLab's product suite — Acadmax, UniCRM.ai, HelloSir.ai, VisaInterviews.ai, TeachingSoftware.ai and Libmax — across the UK, EU, GCC, South Asia and North America.",
    ideal: [
      "Regional software distributors",
      "EdTech & admissions consultancies",
      "Digital agencies with enterprise clientele",
      "Country / territory reseller partners",
    ],
    perks: [
      "Aggressive revenue-share and margin protection",
      "Ready-made sales collateral and demo environments",
      "Territory rights and lead protection",
      "Dedicated partner enablement and quarterly reviews",
    ],
    color: "secondary",
  },
  {
    id: "backoffice",
    icon: Layers,
    kicker: "Program 03",
    title: "Backoffice Partnership",
    tagline: "You stay client-facing. We deliver silently in the background.",
    description:
      "For agencies and consultancies that own the client relationship but lack the in-house AI, engineering or delivery bandwidth. CloudLab becomes your white-labelled backoffice — designing, building and shipping under your brand while you stay on the front line.",
    ideal: [
      "Digital & marketing agencies missing dev capacity",
      "Boutique consultancies short on AI engineering",
      "System integrators needing overflow delivery",
      "Founders scaling a services business without full-time hires",
    ],
    perks: [
      "Fully white-labelled — your brand on every deliverable",
      "NDA-first working model, no client poaching ever",
      "Dedicated pod with a named lead engineer per account",
      "Flexible commercial models — fixed, T&M or retainer",
    ],
    color: "primary",
  },
];

const STATS = [
  { icon: Globe2, value: "6+", label: "Countries served" },
  { icon: Handshake, value: "12", label: "Active partners" },
  { icon: TrendingUp, value: "3×", label: "Avg. pipeline lift" },
  { icon: Percent, value: "30%", label: "Rev-share on qualified deals" },
];

const HOW_IT_WORKS = [
  { n: "01", title: "Introduction call", desc: "30-minute chat to understand your market, capabilities and where CloudLab fits." },
  { n: "02", title: "Program selection", desc: "We shape the right agreement — Technology or Channel — with clear commercials." },
  { n: "03", title: "Enablement", desc: "Product training, joint architecture playbooks, demo environments and sales collateral." },
  { n: "04", title: "Go-live", desc: "First joint pitch inside 30 days, backed by CloudLab engineering pods on every deal." },
];

export default function Partnership() {
  const [heroVideo, setHeroVideo] = useState("");

  useEffect(() => {
    let mounted = true;
    api.get("/page-media/partnership-hero")
      .then((r) => { if (mounted) setHeroVideo(r.data?.video_url || ""); })
      .catch(() => { if (mounted) setHeroVideo(""); });
    return () => { mounted = false; };
  }, []);

  const openPartner = (typeId) =>
    window.dispatchEvent(new CustomEvent("open-partner-drawer", { detail: { types: typeId ? [typeId] : [] } }));

  return (
    <div data-testid="partnership-page" className="overflow-hidden">
      <Seo
        title="Partnership · Build with CloudLab"
        description="Three partnership programs: Technology Partners co-build on our AI stack; Channel & Reseller Partners take CloudLab products to market; Backoffice Partners get us as their white-labelled engineering team."
        keywords="partner with CloudLab, reseller program, channel partner Pakistan UK, technology partnership, AI reseller, SaaS reseller, white label agency backoffice, outsourced engineering partner"
      />

      {/* ============ HERO — balanced 2-col: text left, video right ============ */}
      <section className="relative overflow-hidden">
        <GlowOrb className="-top-32 -right-32" color="primary" size={520} />
        <GlowOrb className="-bottom-32 -left-32" color="secondary" size={420} />
        <div className="relative max-w-7xl mx-auto px-5 md:px-6 pt-10 pb-12 md:pt-16 md:pb-20 grid lg:grid-cols-2 gap-8 lg:gap-14 items-center">
          {/* LEFT — copy + CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 text-[10px] md:text-[11px] font-mono uppercase tracking-[0.3em] text-primary bg-primary/10 rounded-full px-3 py-1.5 border border-primary/30">
              <Sparkles className="w-3.5 h-3.5" /> Partnership Program
            </div>
            <h1 className="mt-4 md:mt-5 font-heading font-black tracking-tighter text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.02]">
              Grow with the AI team behind ambitious brands.
            </h1>
            <p className="mt-4 md:mt-5 text-sm md:text-base text-muted-foreground leading-relaxed max-w-xl">
              Whether you build products, resell them, or need a silent backoffice — join one of our three partnership programs and ship AI-native outcomes worldwide.
            </p>
            <div className="mt-6 md:mt-8 flex flex-wrap items-center gap-3">
              <button
                onClick={() => openPartner()}
                data-testid="partnership-cta-primary"
                className="group rounded-full pl-6 pr-2 py-2 bg-primary text-primary-foreground text-sm font-medium inline-flex items-center gap-3 shadow-[0_20px_50px_-15px_hsl(var(--primary)/0.7)]"
              >
                <span>Become a partner</span>
                <span className="w-8 h-8 rounded-full bg-background text-primary flex items-center justify-center transition-transform group-hover:translate-x-0.5">
                  <ArrowRight className="w-4 h-4" />
                </span>
              </button>
              <a
                href="#programs"
                data-testid="partnership-cta-secondary"
                className="rounded-full px-6 py-3 border border-border text-sm font-medium hover:bg-foreground/5 transition-colors"
              >
                Explore programs
              </a>
            </div>
            <div className="mt-6 md:mt-8 inline-flex items-center gap-2 rounded-full bg-foreground/[0.04] border border-border px-3 py-1.5 text-[11px] font-mono uppercase tracking-[0.25em] text-muted-foreground">
              <Radio className="w-3 h-3 text-primary animate-pulse" /> Three programs · live worldwide
            </div>
          </motion.div>

          {/* RIGHT — landscape video (or aurora fallback) */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.1 }}
            className="relative"
          >
            <div className="relative aspect-video w-full rounded-[24px] md:rounded-[28px] overflow-hidden border border-border bg-slate-950 shadow-[0_35px_80px_-25px_rgba(0,0,0,0.4)]">
              {heroVideo ? (
                <HeroVideo src={heroVideo} testid="partnership-hero-video" />
              ) : (
                <AuroraFallback />
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ============ STATS STRIP ============ */}
      <section className="relative border-y border-border py-10 md:py-12 bg-background">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {STATS.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.06 }}
              className="flex items-start gap-3"
              data-testid={`partnership-stat-${i}`}
            >
              <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                <s.icon className="w-4 h-4" />
              </div>
              <div>
                <div className="font-heading font-black text-2xl md:text-3xl tracking-tight leading-none">
                  {s.value}
                </div>
                <div className="mt-1 text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">
                  {s.label}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ============ TWO PROGRAMS ============ */}
      <section id="programs" className="relative py-20 md:py-28">
        <GlowOrb className="top-1/3 -right-40" color="primary" size={480} />
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="max-w-3xl">
            <div className="text-xs font-mono uppercase tracking-[0.3em] text-primary">The Programs</div>
            <h2 className="mt-3 font-heading font-black tracking-tight text-3xl md:text-5xl">
              Three ways to partner with CloudLab.
            </h2>
            <p className="mt-4 text-muted-foreground text-lg leading-relaxed">
              Pick the shape that fits your business — co-build the tech, resell our products, or let us run silently behind your brand. All three are structured for clarity, protected territories and shared upside.
            </p>
          </div>

          <div className="mt-12 grid md:grid-cols-2 xl:grid-cols-3 gap-6">
            {PARTNER_PROGRAMS.map((p, i) => (
              <ProgramCard key={p.id} program={p} index={i} onCta={() => openPartner(p.id)} />
            ))}
          </div>
        </div>
      </section>

      {/* ============ HOW IT WORKS ============ */}
      <section className="relative py-20 md:py-24 border-t border-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-[1fr_2fr] gap-10 items-start">
            <div>
              <div className="text-xs font-mono uppercase tracking-[0.3em] text-primary">The Onboarding</div>
              <h2 className="mt-3 font-heading font-black tracking-tight text-3xl md:text-4xl leading-tight">
                From intro call to first joint deal — in under 30 days.
              </h2>
              <p className="mt-4 text-muted-foreground leading-relaxed">
                Clear commercials, ready-made enablement kits and a real engineering team on the other side of every pitch.
              </p>
            </div>
            <ol className="space-y-3">
              {HOW_IT_WORKS.map((s) => (
                <li
                  key={s.n}
                  data-testid={`partnership-step-${s.n}`}
                  className="flex items-start gap-5 rounded-2xl border border-border bg-card p-5 md:p-6 hover:border-primary/40 transition-colors"
                >
                  <div className="font-heading font-black text-3xl md:text-4xl text-primary/80 leading-none w-16 shrink-0">
                    {s.n}
                  </div>
                  <div>
                    <div className="font-heading font-bold text-lg">{s.title}</div>
                    <div className="mt-1 text-sm text-muted-foreground leading-relaxed">{s.desc}</div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="relative py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="relative overflow-hidden rounded-[32px] bg-foreground text-background p-10 md:p-16">
            <GlowOrb className="-top-32 -right-32" color="primary" size={520} />
            <div className="relative max-w-3xl">
              <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-primary">Let&apos;s partner</div>
              <h2 className="mt-3 font-heading font-black tracking-tight text-3xl md:text-5xl leading-tight">
                Ready to ship together?
              </h2>
              <p className="mt-5 text-background/80 text-base md:text-lg leading-relaxed">
                Send us a note and a senior member of our team will follow up within 24 hours to walk you through the fit.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <button
                  onClick={() => openPartner()}
                  data-testid="partnership-cta-bottom"
                  className="group rounded-full pl-6 pr-2 py-2 bg-primary text-primary-foreground text-sm font-medium inline-flex items-center gap-3"
                >
                  <span>Apply for partnership</span>
                  <span className="w-8 h-8 rounded-full bg-background text-primary flex items-center justify-center transition-transform group-hover:translate-x-0.5">
                    <ArrowRight className="w-4 h-4" />
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

/* ---------- Program card ---------- */
function ProgramCard({ program: p, index, onCta }) {
  const Icon = p.icon;
  return (
    <motion.article
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6, delay: index * 0.08 }}
      data-testid={`partner-program-${p.id}`}
      className="group relative rounded-3xl border border-border bg-card overflow-hidden"
    >
      {/* accent stripe */}
      <div className={`absolute inset-x-0 top-0 h-1 bg-${p.color}`} />
      <div className="p-8 md:p-10">
        <div className="flex items-center justify-between gap-4">
          <div className="inline-flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
              <Icon className="w-5 h-5" />
            </div>
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-muted-foreground">{p.kicker}</div>
          </div>
          <LineChart className="w-5 h-5 text-muted-foreground/60 group-hover:text-primary transition-colors" />
        </div>

        <h3 className="mt-6 font-heading font-black text-3xl md:text-4xl tracking-tight leading-tight">
          {p.title}
        </h3>
        <p className="mt-2 text-base md:text-lg text-primary font-medium">{p.tagline}</p>
        <p className="mt-4 text-sm text-muted-foreground leading-relaxed">{p.description}</p>

        <div className="mt-8 grid sm:grid-cols-2 xl:grid-cols-1 gap-6">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-muted-foreground mb-3">Ideal for</div>
            <ul className="space-y-2 text-sm">
              {p.ideal.map((it) => (
                <li key={it} className="flex items-start gap-2 leading-snug">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary/70 shrink-0" />
                  <span className="text-foreground/85">{it}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-muted-foreground mb-3">What you get</div>
            <ul className="space-y-2 text-sm">
              {p.perks.map((perk) => (
                <li key={perk} className="flex items-start gap-2 leading-snug">
                  <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                  <span className="text-foreground/85">{perk}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <button
          onClick={onCta}
          data-testid={`partner-program-${p.id}-cta`}
          className="mt-9 inline-flex items-center gap-2 text-sm font-medium text-primary hover:gap-3 transition-all"
        >
          Apply to this program <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </motion.article>
  );
}

/* ---------- Fallback hero (when no video URL is set) ---------- */
function AuroraFallback() {
  return (
    <div className="absolute inset-0">
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(60% 60% at 20% 30%, hsl(var(--primary) / 0.55), transparent 65%), radial-gradient(50% 50% at 80% 70%, hsl(var(--secondary) / 0.45), transparent 65%), linear-gradient(180deg, #050810 0%, #0b1220 100%)",
        }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-40 mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.6'/></svg>\")",
        }}
      />
    </div>
  );
}
